"use client"

import { useState, useEffect } from "react"
import MnemonicCard from "../components/MnemonicCard"
import SearchBar from "../components/SearchBar"
import { sampleMnemonics } from "../data/mnemonics"
import { Zap, BookOpen, Calculator } from "lucide-react"

const SubjectPage = ({ subject }) => {
  const [mnemonics, setMnemonics] = useState([])
  const [searchTerm, setSearchTerm] = useState("")

  // Get subject-specific styling and icon
  const getSubjectInfo = () => {
    switch (subject) {
      case "physics":
        return {
          title: "Physics Mnemonics",
          description: "Master physics concepts with these powerful memory aids",
          icon: <Zap className="w-8 h-8 text-blue-500" />,
          bgColor: "bg-blue-50",
          textColor: "text-blue-600",
        }
      case "chemistry":
        return {
          title: "Chemistry Mnemonics",
          description: "Remember chemical formulas and reactions with ease",
          icon: <BookOpen className="w-8 h-8 text-green-500" />,
          bgColor: "bg-green-50",
          textColor: "text-green-600",
        }
      case "math":
        return {
          title: "Math Mnemonics",
          description: "Simplify complex mathematical concepts and formulas",
          icon: <Calculator className="w-8 h-8 text-orange-500" />,
          bgColor: "bg-orange-50",
          textColor: "text-orange-600",
        }
      default:
        return {
          title: "Subject Mnemonics",
          description: "Learn with powerful memory techniques",
          icon: null,
          bgColor: "bg-gray-50",
          textColor: "text-gray-600",
        }
    }
  }

  const subjectInfo = getSubjectInfo()

  // Filter mnemonics by subject
  useEffect(() => {
    const filtered = sampleMnemonics.filter((mnemonic) => mnemonic.subject.toLowerCase() === subject.toLowerCase())
    setMnemonics(filtered)
  }, [subject])

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term)

    if (term === "") {
      const filtered = sampleMnemonics.filter((mnemonic) => mnemonic.subject.toLowerCase() === subject.toLowerCase())
      setMnemonics(filtered)
    } else {
      const filtered = sampleMnemonics.filter(
        (mnemonic) =>
          mnemonic.subject.toLowerCase() === subject.toLowerCase() &&
          (mnemonic.concept.toLowerCase().includes(term.toLowerCase()) ||
            mnemonic.chapter.toLowerCase().includes(term.toLowerCase()) ||
            mnemonic.mnemonic.toLowerCase().includes(term.toLowerCase())),
      )
      setMnemonics(filtered)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Subject Banner */}
      <div className={`${subjectInfo.bgColor} rounded-lg p-6 mb-8 top-16 z-5 shadow-sm`}>
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-white rounded-full shadow-sm">{subjectInfo.icon}</div>
          <div>
            <h1 className={`text-2xl font-bold ${subjectInfo.textColor}`}>{subjectInfo.title}</h1>
            <p className="text-gray-600">{subjectInfo.description}</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-8">
        <SearchBar onSearch={handleSearch} placeholder={`Search ${subject} mnemonics...`} />
      </div>

      {/* Mnemonics Grid */}
      {mnemonics.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mnemonics.map((mnemonic) => (
            <MnemonicCard key={mnemonic.id} mnemonic={mnemonic} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No {subject} mnemonics found. Try a different search term.</p>
        </div>
      )}
    </div>
  )
}

export default SubjectPage
